import httpClient from "../../../../shared/api/httpClient";
import { qs } from "../../../../shared/api/qs";

const CONTENTS_BASE_PATH = "/hr/areas/contents";

function getLicenseIdOrThrow() {
  try {
    const raw = localStorage.getItem("sabi_empresa_id");
    if (!raw) throw new Error();

    const licenseId = Number(raw);
    if (!Number.isFinite(licenseId) || licenseId <= 0) throw new Error();

    return licenseId;
  } catch {
    throw new Error("No se encontró sabi_empresa_id en localStorage (licencia requerida).");
  }
}

function normalizeId(value) {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function normalizeIdList(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (!Array.isArray(value)) return null;

  const ids = value.map(normalizeId).filter(Boolean);
  return Array.from(new Set(ids));
}

function normalizeNumberOrNull(value) {
  if (value === undefined || value === null || value === "") return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function mapScopeItem(item) {
  return {
    id: Number(item?.id),
    name: item?.name ?? "",
  };
}

function mapViewer(item) {
  return {
    employeeId: Number(item?.employeeId),
    fullName: item?.fullName ?? "",
    lastViewedAt: item?.lastViewedAt ?? null,
    viewedCurrentPeriod: Boolean(item?.viewedCurrentPeriod),
    totalViews: Number(item?.totalViews ?? 0),
  };
}

function mapViewsSummary(summary) {
  return {
    totalTargetEmployees: Number(summary?.totalTargetEmployees ?? 0),
    viewedCurrentPeriod: Number(summary?.viewedCurrentPeriod ?? 0),
    pendingCurrentPeriod: Number(summary?.pendingCurrentPeriod ?? 0),
    totalViews: Number(summary?.totalViews ?? 0),
  };
}

function mapScope(scope) {
  if (!scope) return null;

  return {
    branchIds: normalizeIdList(scope.branchIds),
    areaIds: normalizeIdList(scope.areaIds),
    roleIds: normalizeIdList(scope.roleIds),
    branches: Array.isArray(scope.branches) ? scope.branches.map(mapScopeItem) : [],
    areas: Array.isArray(scope.areas) ? scope.areas.map(mapScopeItem) : [],
    roles: Array.isArray(scope.roles) ? scope.roles.map(mapScopeItem) : [],
    isAllBranches: Boolean(scope.isAllBranches),
    isAllAreas: Boolean(scope.isAllAreas),
    isAllRoles: Boolean(scope.isAllRoles),

    readRequirement: scope.readRequirement ?? "NONE",
    readRepeatEvery: normalizeNumberOrNull(scope.readRepeatEvery),
    readWeekday: normalizeNumberOrNull(scope.readWeekday),
    readMonthDay: normalizeNumberOrNull(scope.readMonthDay),
    readUntil: scope.readUntil ?? null,

    currentPeriodKey: scope.currentPeriodKey ?? null,
    currentPeriodStart: scope.currentPeriodStart ?? null,
    currentPeriodEnd: scope.currentPeriodEnd ?? null,

    viewsSummary: mapViewsSummary(scope.viewsSummary),
    viewers: Array.isArray(scope.viewers) ? scope.viewers.map(mapViewer) : [],
  };
}

function mapContent(item) {
  if (!item) return null;

  return {
    id: Number(item.id),
    title: item.title ?? "",
    category: item.category ?? "",
    status: item.status ?? "",
    body: item.body ?? "",
    branchIds: normalizeIdList(item.branchIds),
    areaIds: normalizeIdList(item.areaIds),
    roleIds: normalizeIdList(item.roleIds),

    readRequirement: item.readRequirement ?? "NONE",
    readRepeatEvery: normalizeNumberOrNull(item.readRepeatEvery),
    readWeekday: normalizeNumberOrNull(item.readWeekday),
    readMonthDay: normalizeNumberOrNull(item.readMonthDay),
    readUntil: item.readUntil ?? null,

    publishedAt: item.publishedAt ?? null,
    archivedAt: item.archivedAt ?? null,
    createdAt: item.createdAt ?? null,
    updatedAt: item.updatedAt ?? null,
    authorDisplayName: item.authorDisplayName ?? null,

    author: item.author
      ? {
          id: Number(item.author.id),
          displayName: item.author.displayName ?? "",
        }
      : null,

    updatedBy: item.updatedBy
      ? {
          id: Number(item.updatedBy.id),
          displayName: item.updatedBy.displayName ?? "",
        }
      : null,

    scope: mapScope(item.scope),
  };
}

function buildContentPayload(data = {}) {
  const payload = {};

  if (data.title !== undefined) payload.title = String(data.title ?? "");
  if (data.category !== undefined) payload.category = data.category;
  if (data.status !== undefined) payload.status = data.status;
  if (data.body !== undefined) payload.body = data.body;

  if ("branchIds" in data) payload.branchIds = normalizeIdList(data.branchIds);
  if ("areaIds" in data) payload.areaIds = normalizeIdList(data.areaIds);
  if ("roleIds" in data) payload.roleIds = normalizeIdList(data.roleIds);

  if ("readRequirement" in data) payload.readRequirement = data.readRequirement ?? "NONE";

  if ("readRepeatEvery" in data) {
    payload.readRepeatEvery = normalizeNumberOrNull(data.readRepeatEvery);
  }

  if ("readWeekday" in data) {
    payload.readWeekday = normalizeNumberOrNull(data.readWeekday);
  }

  if ("readMonthDay" in data) {
    payload.readMonthDay = normalizeNumberOrNull(data.readMonthDay);
  }

  if ("readUntil" in data) {
    payload.readUntil = data.readUntil || null;
  }

  return payload;
}

function mapReferenceItem(item) {
  return {
    id: Number(item?.id),
    name: item?.name ?? "",
  };
}

export async function listContenidos(filters = {}, opts) {
  const query = qs({
    page: filters.page ?? 1,
    limit: filters.limit ?? 100,
    search: filters.search,
    category: filters.category,
    status: filters.status,
  });

  const response = await httpClient.get(`${CONTENTS_BASE_PATH}${query}`, opts);

  return {
    items: Array.isArray(response?.items) ? response.items.map(mapContent) : [],
    page: Number(response?.page ?? 1),
    limit: Number(response?.limit ?? filters.limit ?? 100),
    total: Number(response?.total ?? 0),
  };
}

export async function getContenido(contentId, opts) {
  if (!contentId) throw new Error("contentId es requerido");
  const response = await httpClient.get(`${CONTENTS_BASE_PATH}/${contentId}`, opts);
  return mapContent(response);
}

export async function getContenidoScope(contentId, opts) {
  if (!contentId) throw new Error("contentId es requerido");
  const response = await httpClient.get(`${CONTENTS_BASE_PATH}/${contentId}/scope`, opts);
  return mapScope(response);
}

export async function createContenido(data, opts) {
  const response = await httpClient.post(CONTENTS_BASE_PATH, buildContentPayload(data), opts);
  return mapContent(response);
}

export async function updateContenido(contentId, data, opts) {
  if (!contentId) throw new Error("contentId es requerido");

  const response = await httpClient.patch(
    `${CONTENTS_BASE_PATH}/${contentId}`,
    buildContentPayload(data),
    opts
  );

  return mapContent(response);
}

export async function deleteContenido(contentId, opts) {
  if (!contentId) throw new Error("contentId es requerido");
  return httpClient.delete(`${CONTENTS_BASE_PATH}/${contentId}`, opts);
}

export async function listContenidoBranches(opts) {
  const licenseId = getLicenseIdOrThrow();
  const response = await httpClient.get(`/admin/licenses/${licenseId}/branches`, opts);
  return Array.isArray(response) ? response.map(mapReferenceItem) : [];
}

export async function listContenidoAreas(opts) {
  const licenseId = getLicenseIdOrThrow();
  const response = await httpClient.get(`/admin/licenses/${licenseId}/areas`, opts);
  return Array.isArray(response) ? response.map(mapReferenceItem) : [];
}

export async function listContenidoRoles(opts) {
  const licenseId = getLicenseIdOrThrow();
  const response = await httpClient.get(`/admin/licenses/${licenseId}/roles`, opts);
  return Array.isArray(response) ? response.map(mapReferenceItem) : [];
}